import React, { FC, useContext, useMemo, useState } from "react";
import { Font,  Page, Text, View, Document, StyleSheet, Image, pdf } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { App, Button, ButtonProps } from "antd";
import JsBarcode from "jsbarcode";
import convertTo2DArray from "@/utils/convert2double";
import { getFileUrl } from "@/apis/file";
import { DictContext } from "@/layout/context/DictContext";
import { OrderItemV2, OrderV2 } from "@/types/inventoryV2";
import zioncom from 'images/system/zioncom.png';
import { numFormmater } from "@/pages/Requirement/util";
import { dateFormat } from "@/utils/format";
import dayjs from "dayjs";
import fontSrc from '@/assets/fonts/msyh.ttf';
import vnSrc from '@/assets/fonts/NotoSans-Regular.ttf';
import krSrc from '@/assets/fonts/NotoSansKR-Regular.otf';

// 注册支持中文的字体（重复注册幂等；若启动预热失败，首次渲染时在此兜底加载）
Font.register({
  family: "NotoSansSC",
  src: fontSrc
});

Font.register({
  family: "VnFontSrc",
  src: vnSrc
});

Font.register({
  family: "KoFontSrc",
  src: krSrc
});

// 定义 PDF 文档样式
const styles = StyleSheet.create({
  page: {
    position: "relative",
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    padding: 28
  },

  table: {
    display: "table" as any,
    width: "100%",
    borderStyle: "solid",
    borderTopWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderColor: "black"
  },
  tableRow: {
    borderLeftWidth: 1,
    flexDirection: "row"
  },

  tableCol: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 2,
    fontSize: 6,
    letterSpacing: -1,
    color: 'black'
  },

  fontStyle: {
    textAlign: 'center'
  },

  pageTitle: {
    fontSize: 14,
    textAlign: 'center',
    letterSpacing: 0,
    paddingTop: 8,
    paddingBottom: 16,
  },

  titleSection: {
    position: 'relative' as any,
  },

  titleBarcode: {
    position: 'absolute' as any,
    right: 0,
    top: 0,
    flexDirection: 'column',
    alignItems: 'center'
  },

  barcodeText: {
    fontSize: 5,
    marginTop: -3
  },

  footer: {
    position: "absolute",
    bottom: 24,
    left: 0,
    right: 0,
    fontSize: 6,
  },

  rowInfo: {
    width: "100%",
    flexDirection: "row",
    marginTop: 12,
    paddingHorizontal: 24
  },
  rowItem: {
    flex: 1,
    textAlign: "left"
  },
  rowText: {
    fontSize: 8
  },
});

// 填充空格
function getBlankWord(word: string, type = false) {
  if (type) return String(word).replace(/./g, (word) => word + ' ');
  else return String(word).replace(/./g, (word) => word + '');
}

interface PrintSaleInProps extends ButtonProps {
  order: OrderV2;
  btnText?: string;
  disabled: boolean;
  onStart: () => void;
  onEnd: () => void;
}

type CodeUrlOrderItem = OrderItemV2 & {
  codeUrl: string;
  codeStr: string;
};

const PrintSaleIn: FC<PrintSaleInProps> = ({
  order, disabled, onStart, onEnd, btnText, ...restProps
}) => {
  const { t } = useTranslation();
  const { printSetting } = useContext(DictContext);
  const { notification } = App.useApp();
  const [loading, setLoading] = useState<boolean>(false);

  const TABLE_SIZE = 20;

  const { picUrl, name } = useMemo(() => {
    let [picUrl, name] = ['', ''];
    if (printSetting?.length > 0) {
      printSetting.forEach(item => {
        if (item.key === "companyName" && item.value) name = item.value;
        if (item.key === "companyLogo" && item.value) picUrl = getFileUrl(item.value) || '';
      });
    }
    return { picUrl, name };
  }, [printSetting, order, t]);

  const handleCode = (orderNo: string): Promise<{ codeUrl: string; codeStr: string }> => {
    return new Promise((resolve, reject) => {
      const canvasDom = document.createElement("canvas");
      JsBarcode(canvasDom, orderNo, {
        format: "CODE128",
        width: 10,
        height: 28,
        displayValue: false,
        margin: 0,
        marginBottom: 8
      });
      canvasDom.toBlob((blob) => {
        canvasDom.remove();
        if (blob) resolve({ codeUrl: URL.createObjectURL(blob), codeStr: orderNo });
        else reject("条码生成失败");
      }, 'image/png');
    });
  };

  const MyDocument = (selRows: CodeUrlOrderItem[], orderBarcode: string) => {
    if (selRows && selRows.length > 0) {
      let seq = 0;
      let littleTotal: number[] = [];
      let total: number = 0;

      for (let i = 0; i < selRows.length; i += TABLE_SIZE) {
        const chunk = selRows.slice(i, i + TABLE_SIZE);
        const sum = chunk.reduce((acc, item) => acc + item.count, 0);
        littleTotal.push(sum);
      }
      littleTotal.forEach(count => total += count)

      return <Document>
        <Page size="A4" orientation="landscape" style={{ ...styles.page }} wrap>
          <View >
            <View style={styles.titleSection}>
              <Text style={{ ...styles.fontStyle, ...styles.pageTitle }}>
                {name || '吉翁科技（深圳）有限公司'}
              </Text>
              <Text style={{ fontSize: 10, textAlign: "center", marginTop: -14 }}>
                {t('sale.prodIn', { lng: "zh" })}/{t('sale.prodIn', { lng: "vn" })}
              </Text>
              <View style={styles.titleBarcode}>
                <Image src={orderBarcode} style={{ width: 100, height: 28 }} />
                <Text style={styles.barcodeText}>{order.number}</Text>
              </View>
            </View>

            <View style={[styles.rowInfo, { marginTop: 0, marginBottom: 8 }]}>
              <View style={[{ width: "33.33%" }]}>
                <Text style={styles.rowText}>
                  {`${t('sale.inboundNo', { lng: "zh" })}(${t('sale.inboundNo', { lng: "vn" })}) ${order.number}`}
                </Text>
              </View>
              <View style={[{ width: "33.33%" }]}>
                <Text style={styles.rowText}>
                  {`${t('sale.inboundDate', { lng: "zh" })}(${t('sale.inboundDate', { lng: "vn" })}) ${order.confirmedAt ? dateFormat(dayjs(order.confirmedAt)) : ''}`}
                </Text>
              </View>
              <View style={[{ width: "33.33%" }]}>
                <Text style={styles.rowText}>
                  {`${t('sale.warehouse', { lng: "zh" })}(${t('sale.warehouse', { lng: "vn" })}) ${t('sale.finishedWarehouse', { lng: "zh" })}/${t('sale.finishedWarehouse', { lng: "vn" })}`}
                </Text>
              </View>
            </View>
            <View style={[styles.rowInfo, { marginTop: 0, marginBottom: 8 }]}>
              <View style={[{ width: "33.33%" }]}>
                <Text style={styles.rowText}>
                  {`${t('sale.inboundType', { lng: "zh" })}(${t('sale.inboundType', { lng: "vn" })})`} {t('warehouse.proIn', { lng: "zh" })}/{t('warehouse.proIn', { lng: "vn" })}
                </Text>
              </View>
              <View style={[{ width: "33.33%" }]}>
                <Text style={styles.rowText}>
                  {`${t('sale.origin', { lng: "zh" })}(${t('sale.origin', { lng: "vn" })})`}
                </Text>
              </View>
            </View>
          </View>

          <View style={{ ...styles.table, flexShrink: 0 }}>
            {/* 表头 */}
            <View style={{ flexDirection: 'row' }} fixed render={({ pageNumber }) => {
              return pageNumber === 1 ? null : <View style={{ height: 1, backgroundColor: "#000", width: "100%" }} />
            }}></View>
            <View style={styles.tableRow} fixed wrap={false}>
              <View style={{ ...styles.tableCol, width: '5%' }}>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.orderNo', { lng: "zh" }), true)}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.orderNo', { lng: "vn" }), true)}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '10%' }}>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.prodOrderNo', { lng: "zh" }), true)}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.prodOrderNo', { lng: "vn" }), true)}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '15%' }}>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.oldCode', { lng: "zh" }), true)}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.oldCode', { lng: "vn" }), true)}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '10%' }}>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.prodCode', { lng: "zh" }), true)}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.prodCode', { lng: "vn" }), true)}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '10%' }}>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.prodName', { lng: "zh" }), true)}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.prodName', { lng: "vn" }), true)}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '25%' }}>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.prodMod', { lng: "zh" }), true)}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.prodMod', { lng: "vn" }), true)}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '10%' }}>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.count', { lng: "zh" }), true)}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.count', { lng: "vn" }), true)}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '5%' }}>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.uom', { lng: "zh" }), true)}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.uom', { lng: "vn" }), true)}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '10%' }}>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.batch', { lng: "zh" }), true)}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.batch', { lng: "vn" }), true)}</Text>
              </View>
            </View>

            {/* 表身 */}
            {selRows.map((item, rowIndex) => (
              <View style={styles.tableRow} key={rowIndex} wrap={false}>
                <View style={{ ...styles.tableCol, width: '5%' }} />
                <View style={{ ...styles.tableCol, width: '10%' }} />
                <View style={{ ...styles.tableCol, width: '15%' }} />
                <View style={{ ...styles.tableCol, width: '10%' }}>
                  <Text style={styles.fontStyle}>{getBlankWord(item.product?.code || '', true)}</Text>
                </View>
                <View style={{ ...styles.tableCol, width: '10%' }}>
                  <Text style={{ ...styles.fontStyle, textAlign: 'left' }}>
                    {getBlankWord(item.product?.name || '', true)}
                  </Text>
                </View>
                <View style={{ ...styles.tableCol, width: '25%' }}>
                  <Text style={{ ...styles.fontStyle, textAlign: 'left', width: "100%" }}>
                    {getBlankWord(item.product?.model || '', true)}
                  </Text>
                </View>
                <View style={{ ...styles.tableCol, width: '10%' }}>
                  <Text style={{ ...styles.fontStyle, letterSpacing: 0 }}>{numFormmater(item.count ?? 0)}</Text>
                </View>
                <View style={{ ...styles.tableCol, width: '5%' }}>
                  <Text style={styles.fontStyle}>{getBlankWord(item.product?.uom || '', true)}</Text>
                </View>
                <View style={{ ...styles.tableCol, width: '10%' }}>
                  <Text style={styles.fontStyle}>{getBlankWord(item.lot ?? '', true)}</Text>
                </View>
              </View>
            ))}

            {/* 合计 */}
            <View style={styles.tableRow}>
              <View style={{ ...styles.tableCol, width: '15%' }} >
                <Text style={{ ...styles.fontStyle, textAlign: 'left' }}>
                  {getBlankWord(`${t('sale.littleTotal', { lng: "zh" })}(${t('sale.littleTotal', { lng: "vn" })})`, true)}
                </Text>
              </View>
              <View style={{ ...styles.tableCol, width: '15%' }} />
              <View style={{ ...styles.tableCol, width: '10%' }} />
              <View style={{ ...styles.tableCol, width: '10%' }} />
              <View style={{ ...styles.tableCol, width: '25%' }} />
              <View style={{ ...styles.tableCol, width: '10%' }}>
                <Text style={{ ...styles.fontStyle, letterSpacing: 0 }}>{numFormmater(++seq)}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '5%' }} />
              <View style={{ ...styles.tableCol, width: '10%' }} />
            </View>
            <View style={styles.tableRow}>
              <View style={{ ...styles.tableCol, width: '15%' }} >
                <Text style={{ ...styles.fontStyle, textAlign: 'left' }}>
                  {getBlankWord(`${t('sale.total', { lng: "zh" })}(${t('sale.total', { lng: "vn" })})`, true)}
                </Text>
              </View>
              <View style={{ ...styles.tableCol, width: '15%' }} />
              <View style={{ ...styles.tableCol, width: '10%' }} />
              <View style={{ ...styles.tableCol, width: '10%' }} />
              <View style={{ ...styles.tableCol, width: '25%' }} />
              <View style={{ ...styles.tableCol, width: '10%' }}>
                <Text style={{ ...styles.fontStyle, letterSpacing: 0 }}>{numFormmater(total ?? 0)}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '5%' }} />
              <View style={{ ...styles.tableCol, width: '10%' }} />
            </View>
          </View>

          <View style={[styles.rowInfo, { marginTop: 0 }]}>
            <View style={styles.rowItem}>
              <Text style={styles.rowText}>
                {`${t('sale.origin', { lng: "zh" })}(${t('sale.origin', { lng: "vn" })})：`}
              </Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowText}>
                {`${t('sale.productor', { lng: "zh" })}(${t('sale.productor', { lng: "vn" })})：`}
              </Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowText}>
                {`${t('sale.confirmer', { lng: "zh" })}(${t('sale.confirmer', { lng: "vn" })})：`}
              </Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowText}>
                {`${t('sale.prodManager', { lng: "zh" })}(${t('sale.prodManager', { lng: "vn" })})：`}
              </Text>
            </View>
          </View>

          <View style={[styles.rowInfo]}>
            <View style={styles.rowItem}>
              <Text style={styles.rowText}>
                {`${t('sale.origin', { lng: "zh" })}(${t('sale.origin', { lng: "vn" })})：`}
              </Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowText}>
                {`${t('sale.warehouseer', { lng: "zh" })}(${t('sale.warehouseer', { lng: "vn" })})：`}
              </Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowText}>
                {`${t('sale.approver', { lng: "zh" })}(${t('sale.approver', { lng: "vn" })})：`}
              </Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowText}>
                {`${t('sale.checker', { lng: "zh" })}(${t('sale.checker', { lng: "vn" })})：`}
              </Text>
            </View>
          </View>

          <Image src={picUrl || zioncom} style={{
            position: "absolute",
            top: 24,
            left: 30,
            height: 30,
            objectFit: "scale-down"
          }} />

          <Text fixed style={{ ...styles.fontStyle, ...styles.footer, zIndex: 99 }} render={({ pageNumber, totalPages }) => {
            return `P ${pageNumber} / ${totalPages}`;
          }} />
        </Page>
      </Document>;
    }
    return <Document />;
  };

  const handlePDF = async () => {
    if (!order.items || order.items.length <= 0) {
      return notification.error({
        placement: 'top',
        message: t('common.addError'),
        description: '暂无数据可打印',
      });
    }

    setLoading(true);
    onStart();

    try {
      // 生成订单号条码
      let orderBarcode: string | undefined;
      try {
        const barcodeData = await handleCode(order.number);
        orderBarcode = barcodeData.codeUrl;
      } catch (error) {
        console.error('订单号条码生成失败:', error);
      }

      // 生成每个 item 的条码（如果需要的话）
      let codeRows: CodeUrlOrderItem[] = order.items.map(item => ({
        ...item,
        codeUrl: '',
        codeStr: ''
      }));

      let pageEle = MyDocument(codeRows, orderBarcode || '');
      if (!pageEle) {
        setLoading(false);
        onEnd();
        return notification.error({ placement: 'top', message: '打印失败' });
      }

      // Generate PDF as Blob in the browser
      const blob = await pdf(pageEle).toBlob();
      const pdfUrl = URL.createObjectURL(blob);

      // 创建一个 iframe 并将 PDF URL 作为其源
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = pdfUrl;

      // 将 iframe 添加到 body 中
      document.body.appendChild(iframe);
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (error) {
          console.error('打印失败:', error);
        } finally {
          setLoading(false);
          onEnd();
        }
      };
    } catch (error) {
      console.error('打印失败:', error);
      setLoading(false);
      onEnd();
    }
  };

  return (
    <Button
      type="primary"
      onClick={handlePDF}
      loading={loading}
      disabled={disabled}
      {...restProps}
    >
      {btnText || t('common.printSaleIn')}
    </Button>
  );
};

export default PrintSaleIn;
